import React from 'react';
import { FaPlus, FaTrash, FaArrowUp, FaArrowDown } from 'react-icons/fa';

const ICON_OPTIONS = [
  { value: 'shield', label: 'Shield (Compliance)' },
  { value: 'headset', label: 'Headset (Support)' },
  { value: 'lock', label: 'Lock (Security)' },
  { value: 'check', label: 'Check Circle' },
  { value: 'chart', label: 'Chart' },
  { value: 'users', label: 'Users / Team' },
  { value: 'globe', label: 'Globe' },
  { value: 'award', label: 'Award / Medal' },
  { value: 'target', label: 'Target' },
];

const BADGE_ICON_OPTIONS = [
  { value: 'check', label: 'Check Circle' },
  { value: 'us-flag', label: 'US Flag' },
  { value: 'none', label: 'No Icon' },
];

const emptyTrustBadge = (index) => ({
  icon: 'shield',
  title: '',
  subtitle: '',
  order: index,
});

const ServiceHeroExtrasEditor = ({ form, updateField }) => {
  const badges = form.heroTrustBadges || [];

  const updateBadge = (index, field, value) => {
    const next = [...badges];
    next[index] = { ...next[index], [field]: value };
    updateField('heroTrustBadges', next);
  };

  const addBadge = () => {
    updateField('heroTrustBadges', [...badges, emptyTrustBadge(badges.length)]);
  };

  const removeBadge = (index) => {
    if (!window.confirm('Remove this trust badge?')) return;
    updateField(
      'heroTrustBadges',
      badges.filter((_, i) => i !== index).map((badge, i) => ({ ...badge, order: i }))
    );
  };

  const moveBadge = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= badges.length) return;
    const next = [...badges];
    [next[index], next[target]] = [next[target], next[index]];
    updateField('heroTrustBadges', next.map((badge, i) => ({ ...badge, order: i })));
  };

  return (
    <div className="space-y-5 border-t border-gray-100 dark:border-dark-700 pt-5">
      <h3 className="text-sm font-bold text-gray-900 dark:text-white">Hero Badge & Trust Icons</h3>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Edit the pill badge above the title and the three icon rows below the hero buttons.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="space-y-1.5">
          <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Top Badge Text</label>
          <input
            className="input-modern"
            value={form.heroBadgeText || ''}
            onChange={(e) => updateField('heroBadgeText', e.target.value)}
            placeholder="Trusted by 1000+ Indian Entrepreneurs"
          />
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Top Badge Icon</label>
          <select
            className="input-modern"
            value={form.heroBadgeIcon || 'check'}
            onChange={(e) => updateField('heroBadgeIcon', e.target.value)}
          >
            {BADGE_ICON_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Trust Badges (under buttons)</label>
        <button
          type="button"
          onClick={addBadge}
          className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg"
        >
          <FaPlus className="w-3 h-3" /> Add Badge
        </button>
      </div>

      {badges.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-4">No trust badges. Click Add Badge or Reload Defaults.</p>
      ) : (
        <div className="space-y-4">
          {badges.map((badge, index) => (
            <div key={badge._id || `badge-${index}`} className="rounded-xl border border-gray-200 dark:border-dark-600 p-4 space-y-3 bg-gray-50/50 dark:bg-dark-900/30">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-bold text-gray-600 dark:text-gray-300">Badge {index + 1}</span>
                <div className="flex items-center gap-1">
                  <button type="button" onClick={() => moveBadge(index, -1)} disabled={index === 0} className="p-1.5 rounded border border-gray-200 dark:border-dark-600 disabled:opacity-40">
                    <FaArrowUp className="w-3 h-3" />
                  </button>
                  <button type="button" onClick={() => moveBadge(index, 1)} disabled={index === badges.length - 1} className="p-1.5 rounded border border-gray-200 dark:border-dark-600 disabled:opacity-40">
                    <FaArrowDown className="w-3 h-3" />
                  </button>
                  <button type="button" onClick={() => removeBadge(index)} className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded">
                    <FaTrash className="w-3 h-3" />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <label className="text-[10px] font-semibold uppercase text-gray-500">Icon</label>
                  <select className="input-modern text-sm" value={badge.icon || 'shield'} onChange={(e) => updateBadge(index, 'icon', e.target.value)}>
                    {ICON_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-semibold uppercase text-gray-500">Title</label>
                  <input className="input-modern text-sm" value={badge.title || ''} onChange={(e) => updateBadge(index, 'title', e.target.value)} placeholder="100% Compliant" />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-semibold uppercase text-gray-500">Subtitle</label>
                  <input className="input-modern text-sm" value={badge.subtitle || ''} onChange={(e) => updateBadge(index, 'subtitle', e.target.value)} placeholder="IRS & State Compliant" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ServiceHeroExtrasEditor;
